import { z } from "zod";
import { reviewStates } from "./review";

const selectionRuleSchema = z
  .object({
    field: z.enum(["status", "type", "source", "assignee"]),
    operator: z.enum(["is", "is_not"]),
    values: z.array(z.string().min(1)).min(1),
  })
  .superRefine((rule, ctx) => {
    if (
      rule.field === "assignee" &&
      rule.values.some((value) => value !== "unassigned" && !z.uuid().safeParse(value).success)
    )
      ctx.addIssue({ code: "custom", message: "Choose a valid assignee." });
    if (
      rule.field === "status" &&
      rule.values.some((value) => !(reviewStates as readonly string[]).includes(value))
    ) {
      ctx.addIssue({ code: "custom", message: "Choose a valid status." });
    }
  });
const textRuleSchema = z.object({
  field: z.literal("text"),
  operator: z.enum(["contains", "not_contains"]),
  value: z.string().trim().min(1),
});
const dateRuleSchema = z
  .object({
    field: z.literal("date"),
    operator: z.enum(["before", "after", "on", "between", "within", "older"]),
    value: z.string().min(1),
    end: z.string().optional(),
    unit: z.enum(["days", "weeks", "months", "years"]).optional(),
  })
  .superRefine((rule, ctx) => {
    if (rule.operator === "within" || rule.operator === "older") {
      if (
        !/^[1-9]\d*$/.test(rule.value) ||
        !Number.isSafeInteger(Number(rule.value)) ||
        !rule.unit
      ) {
        ctx.addIssue({ code: "custom", message: "Choose a positive whole duration and unit." });
      }
    } else if (!z.iso.date().safeParse(rule.value).success || rule.value.startsWith("0000")) {
      ctx.addIssue({ code: "custom", message: "Choose a valid date." });
    } else if (
      rule.operator === "between" &&
      (!z.iso.date().safeParse(rule.end).success || (rule.end ?? "") < rule.value)
    ) {
      ctx.addIssue({ code: "custom", message: "End date must be on or after the start date." });
    }
  });
export const activityRuleSchema = z.union([selectionRuleSchema, textRuleSchema, dateRuleSchema]);
export type ActivityRule = z.infer<typeof activityRuleSchema>;
export type ActivityFilterGroup = { operator: "and" | "or"; rules: ActivityFilterNode[] };
export type ActivityFilterNode = ActivityRule | ActivityFilterGroup;
export const activityFilterGroupSchema: z.ZodType<ActivityFilterGroup> = z.object({
  operator: z.enum(["and", "or"]),
  rules: z.array(z.lazy(() => z.union([activityRuleSchema, activityFilterGroupSchema]))).min(1),
});
export const activityFilterQuerySchema = z
  .string()
  .transform((value, ctx) => {
    try {
      return JSON.parse(value) as unknown;
    } catch {
      ctx.addIssue({ code: "custom", message: "Invalid filter JSON." });
      return z.NEVER;
    }
  })
  .pipe(activityFilterGroupSchema);

/** Bring existing bookmarked filters into the builder without changing their meaning. */
export function activityFilterFromQuery(
  query: Record<string, string | undefined>,
): ActivityFilterGroup {
  const rules: ActivityFilterNode[] = [];
  if (query.filter) rules.push(activityFilterQuerySchema.parse(query.filter));
  for (const [key, field] of [
    ["assignee", "assignee"],
    ["review", "status"],
    ["type", "type"],
    ["repository", "source"],
  ] as const) {
    const value = query[key];
    if (value) rules.push({ field, operator: "is", values: value.split(",") });
  }
  if (query.time) {
    const [operator, value, unit] = query.time.split(":");
    const date = dateRuleSchema.safeParse({ field: "date", operator, value, unit });
    if (date.success) rules.push(date.data);
  } else if (["1", "7", "30"].includes(query.days || "")) {
    rules.push({ field: "date", operator: "within", value: query.days ?? "7", unit: "days" });
  }
  const first = rules[0];
  if (rules.length === 1 && first && "rules" in first) return first;
  return { operator: "and", rules };
}

export function activityFilterCount(node: ActivityFilterNode): number {
  return "rules" in node
    ? node.rules.reduce((count, child) => count + activityFilterCount(child), 0)
    : 1;
}
